import api from '../api/api';

/**
 * Finds or creates the invisible "General Maintenance" project.
 * Supports both HomeUser and BusinessUser.
 * 
 * @param {number|string} userId - The ID of the logged-in user.
 * @param {string} userType - "Home", "HomeUser", "Business", or "BusinessUser".
 * @returns {Promise<number>} - The ProjectId.
 */


export const getOrCreateGeneralMaintenanceProjectId = async (
    userId,
    userType = 'HomeUser'
) => {
    if (!userId) {
        throw new Error('User ID is required');
    }

    // Normalise userType
    const isBusiness = userType.includes('Business');
    const dbUserType = isBusiness ? 'BusinessUser' : 'HomeUser';

    try {
        /* -------------------------------------------------------
           1. Try to GET the existing General Maintenance project
        -------------------------------------------------------- */
        const getResponse = await api.get(
            `/api/Projects/getGMProjectId`,
            {
                params: {
                    userId,
                    userType: dbUserType
                }
            }
        );

        const projectId = getResponse.data;

        // ✅ If it exists, return it immediately
        if (projectId) {
            return projectId;
        }

        /* -------------------------------------------------------
           2. If NOT found, CREATE it
        -------------------------------------------------------- */
        const createPayload = {
            Name: 'General Maintenance',
            Description: 'Container for one-off jobs',
            Postcode: '0000',
            Status: 'Active',
            UserType: dbUserType,
            OwnerUserId: Number(userId),
            MyState: '',
            Suburb: ''
        };

        const createResponse = await api.post(
            '/api/UserProjects',
            createPayload
        );

        const newProjectId =
            createResponse.data?.projectId ||
            createResponse.data?.ProjectId ||
            createResponse.data?.id;

        if (!newProjectId) {
            throw new Error('Failed to create General Maintenance project');
        }

        return newProjectId;

    } catch (error) {
        console.error('Error getting or creating General Maintenance project:', error);
        throw error;
    }
};


export const getOrCreateGeneralProject = async (userId, userType = 'HomeUser') => {
    try {
        if (!userId) throw new Error("User ID is required to get General Project");

        // 1. Normalize UserType for Database (Needs "HomeUser" or "BusinessUser")
        //    and for API Query (Some endpoints might expect "Business")
        const isBusiness = userType.includes('Business');
        const dbUserType = isBusiness ? 'BusinessUser' : 'HomeUser';
        const apiQueryType = isBusiness ? 'BusinessUser' : 'HomeUser';

        // 2. Fetch existing projects
        // Note: We append userType to query to ensure we search the right table/context
        const response = await api.get(`/api/Projects/getmyprojects?homeUserId=${userId}&userType=${apiQueryType}`);
        const projects = response.data || [];

        // 3. Look for the system bucket
        const generalProject = projects.find(p => p.name === "General Maintenance");

        if (generalProject) {
            // Handle variations in ID casing (projectId vs ProjectId)
            return generalProject.projectId || generalProject.ProjectId;
        }

        // 4. If not found, create it (The "Invisible" creation)
        const createPayload = {
            Name: "General Maintenance",
            Description: "Container for one-off jobs",
            Postcode: "0000", // Placeholder
            UserType: dbUserType, // "HomeUser" or "BusinessUser"
            OwnerUserId: parseInt(userId, 10),
            Status: "Active",
            MyState: "",
            Suburb: ""
        };

        const createResponse = await api.post('/api/UserProjects', createPayload);
        
        // Return the new ID
        return createResponse.data.projectId || createResponse.data.ProjectId || createResponse.data.id;

    } catch (error) {
        console.error("Error getting General Project:", error);
        throw error;
    }
};



